import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { BrandLogo } from "@/components/BrandLogo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import {
  LayoutDashboard, ShoppingCart, FileText, Wallet,
  Users, Sparkles, Megaphone, LogOut, Contact, HandCoins, MoreHorizontal, X, KeyRound,
} from "lucide-react";

const NAV = [
  { key: "nav.dashboard", to: "/app/dashboard", icon: LayoutDashboard, roles: ["admin", "owner"], color: "gold", priority: 1 },
  { key: "nav.kasir", to: "/app/kasir", icon: ShoppingCart, roles: ["admin", "owner", "kasir"], color: "leaf", priority: 1 },
  { key: "nav.reports", to: "/app/reports", icon: FileText, roles: ["admin", "owner"], color: "gold", priority: 2 },
  { key: "nav.therapistsReport", to: "/app/therapists-report", icon: HandCoins, roles: ["admin", "owner"], color: "gold", priority: 3 },
  { key: "nav.therapist", to: "/app/earnings", icon: Wallet, roles: ["terapis"], color: "gold", priority: 1 },
  { key: "nav.customers", to: "/app/customers", icon: Contact, roles: ["admin", "owner"], color: "leaf", priority: 2 },
  { key: "nav.services", to: "/app/services", icon: Sparkles, roles: ["admin"], color: "leaf", priority: 3 },
  { key: "nav.announcements", to: "/app/announcements", icon: Megaphone, roles: ["admin", "owner"], color: "leaf", priority: 3 },
  { key: "nav.users", to: "/app/users", icon: Users, roles: ["admin"], color: "gold", priority: 3 },
];

const ROLE_BADGE = {
  admin: { label: "Admin", color: "bg-gold text-charcoal-900" },
  owner: { label: "Owner", color: "bg-leaf text-charcoal-900" },
  kasir: { label: "Kasir", color: "bg-blue-500/80 text-white" },
  terapis: { label: "Terapis", color: "bg-purple-500/80 text-white" },
  pelanggan: { label: "Customer", color: "bg-charcoal-600 text-white" },
};

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes(user?.role));
  // Mobile bottom-nav: top 4 by priority, rest goes to "More" sheet
  const sortedByPriority = [...items].sort((a, b) => a.priority - b.priority);
  const primaryMobile = sortedByPriority.slice(0, 4);
  const overflowMobile = sortedByPriority.slice(4);

  function doLogout() {
    logout();
    nav("/login", { replace: true });
  }

  return (
    <div className="min-h-screen md:flex bg-charcoal-800 text-foreground">
      {/* ───────── DESKTOP / TABLET SIDEBAR ───────── */}
      <aside className="hidden md:flex w-60 lg:w-64 flex-col border-e border-gold/15 bg-charcoal-900/70 backdrop-blur-xl p-4 lg:p-5 sticky top-0 h-screen">
        <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-gold via-leaf to-gold" />
        <BrandLogo size="sm" />
        <nav className="mt-8 lg:mt-10 flex-1 space-y-1 scrollbar-thin overflow-y-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.key.replace("nav.", "")}`}
              data-icon-color={it.color}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              <it.icon className="h-5 w-5" />
              <span className="text-sm lg:text-base">{t(it.key)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-gold/15 pt-4 mt-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-gold to-leaf grid place-items-center text-charcoal-900 font-bold shadow-lg">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
              <span className={`inline-block text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${ROLE_BADGE[user?.role]?.color}`}>
                {ROLE_BADGE[user?.role]?.label}
              </span>
            </div>
          </div>
          <LanguageSwitcher />
          <button
            onClick={() => setPwdOpen(true)}
            data-testid="sidebar-change-password-btn"
            className="w-full mt-2 nav-link justify-start"
          >
            <KeyRound className="h-4 w-4" /> {t("pwd.change")}
          </button>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="w-full mt-1 nav-link justify-start hover:text-red-400"
          >
            <LogOut className="h-4 w-4" /> {t("nav.logout")}
          </button>
        </div>
      </aside>

      {/* ───────── MOBILE TOP BAR ───────── */}
      <header className="md:hidden fixed inset-x-0 top-0 z-40 bg-charcoal-900/95 backdrop-blur-xl border-b border-gold/15">
        <div className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-gold via-leaf to-gold" />
        <div className="px-4 py-3 flex items-center justify-between">
          <BrandLogo size="sm" />
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="ghost" />
            <button
              onClick={() => setProfileOpen(true)}
              data-testid="mobile-profile-btn"
              className="h-9 w-9 rounded-full bg-gradient-to-br from-gold to-leaf grid place-items-center text-charcoal-900 font-bold shadow-lg active:scale-95 transition"
            >
              {user?.name?.charAt(0)?.toUpperCase()}
            </button>
          </div>
        </div>
        <div className={`text-center text-[10px] uppercase tracking-[0.2em] py-1 ${ROLE_BADGE[user?.role]?.color}`}>
          {ROLE_BADGE[user?.role]?.label} · {user?.name}
        </div>
      </header>

      {/* ───────── MAIN ───────── */}
      <main className="w-full md:flex-1 min-w-0 px-4 sm:px-6 md:px-8 lg:px-10 pt-24 md:pt-8 pb-28 md:pb-8">
        {children}
      </main>

      {/* ───────── MOBILE BOTTOM NAV (all roles) ───────── */}
      <nav
        className="md:hidden fixed inset-x-0 bottom-0 z-[60] flex border-t border-gold/15 bg-charcoal-900/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-bottom-nav"
      >
        {primaryMobile.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={`mobile-nav-${it.key.replace("nav.", "")}`}
            data-icon-color={it.color}
            className={({ isActive }) => `bottom-nav-link ${isActive ? "active" : ""}`}
          >
            <it.icon className="h-5 w-5" />
            <span className="truncate max-w-[60px]">{t(it.key)}</span>
          </NavLink>
        ))}
        {overflowMobile.length > 0 && (
          <button
            onClick={() => setMoreOpen(true)}
            data-testid="mobile-nav-more"
            className="bottom-nav-link"
          >
            <MoreHorizontal className="h-5 w-5" />
            <span>{t("common.more") !== "common.more" ? t("common.more") : "More"}</span>
          </button>
        )}
        {overflowMobile.length === 0 && (
          <button onClick={doLogout} data-testid="mobile-nav-logout" className="bottom-nav-link">
            <LogOut className="h-5 w-5" />
            <span>{t("nav.logout")}</span>
          </button>
        )}
      </nav>

      {/* ───────── MORE SHEET ───────── */}
      {moreOpen && (
        <div
          className="md:hidden fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end"
          onClick={() => setMoreOpen(false)}
        >
          <div
            className="w-full bg-charcoal-900 rounded-t-3xl border-t border-gold/30 p-6 pb-8 animate-fade-in-up max-h-[75vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-1 w-12 rounded-full bg-gold/40 mx-auto mb-5" />
            <div className="flex justify-between items-center mb-5">
              <div className="text-xs uppercase tracking-[0.3em] text-gold">Menu</div>
              <button onClick={() => setMoreOpen(false)} data-testid="more-sheet-close" className="p-1">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {overflowMobile.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={() => setMoreOpen(false)}
                  data-testid={`more-nav-${it.key.replace("nav.", "")}`}
                  className={`flex flex-col items-center text-center gap-2 p-4 rounded-xl card-luxury ${
                    it.color === "leaf" ? "accent-leaf" : "accent-gold"
                  } active:scale-95 transition`}
                >
                  <div className={`h-10 w-10 rounded-xl grid place-items-center ${
                    it.color === "leaf" ? "bg-leaf/15 text-leaf" : "bg-gold/15 text-gold"
                  }`}>
                    <it.icon className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-medium">{t(it.key)}</span>
                </NavLink>
              ))}
            </div>
            <button
              onClick={() => { setMoreOpen(false); doLogout(); }}
              data-testid="more-sheet-logout"
              className="w-full mt-5 py-3 rounded-xl border border-red-500/30 text-red-400 font-medium inline-flex items-center justify-center gap-2 active:scale-95 transition"
            >
              <LogOut className="h-4 w-4" /> {t("nav.logout")}
            </button>
          </div>
        </div>
      )}

      {/* ───────── PROFILE SHEET ───────── */}
      {profileOpen && (
        <div
          className="md:hidden fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end"
          onClick={() => setProfileOpen(false)}
        >
          <div
            className="w-full bg-charcoal-900 rounded-t-3xl border-t border-gold/30 p-6 pb-8 animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-1 w-12 rounded-full bg-gold/40 mx-auto mb-5" />
            <div className="flex items-center gap-4 mb-6">
              <div className="h-14 w-14 rounded-full bg-gradient-to-br from-gold to-leaf grid place-items-center text-charcoal-900 font-bold text-xl">
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <div className="text-lg font-heading">{user?.name}</div>
                <span className={`inline-block text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 mt-1 ${ROLE_BADGE[user?.role]?.color}`}>
                  {ROLE_BADGE[user?.role]?.label}
                </span>
              </div>
            </div>
            <button
              onClick={() => { setProfileOpen(false); setPwdOpen(true); }}
              data-testid="profile-change-password-btn"
              className="w-full py-3 rounded-xl border border-gold/30 text-gold font-medium inline-flex items-center justify-center gap-2 active:scale-95 transition mb-2"
            >
              <KeyRound className="h-4 w-4" /> {t("pwd.change")}
            </button>
            <button
              onClick={() => { setProfileOpen(false); doLogout(); }}
              data-testid="profile-logout-btn"
              className="w-full py-3 rounded-xl border border-red-500/30 text-red-400 font-medium inline-flex items-center justify-center gap-2 active:scale-95 transition"
            >
              <LogOut className="h-4 w-4" /> {t("nav.logout")}
            </button>
          </div>
        </div>
      )}
      {/* ───────── CHANGE PASSWORD MODAL ───────── */}
      {pwdOpen && <ChangePasswordModal onClose={() => setPwdOpen(false)} />}
    </div>
  );
}
